import { AHWidget, AHWidgetNoData, AHWidgetTitle, TouchableBox } from "arkh";
import React, { useCallback, useMemo } from "react";

import { BaseWidgetProps } from "../BaseProps";

import { ResultGrid } from "../components/ResultGrid";

import {
  WidgetTaskMultipleResponse,
  WidgetTaskMultipleResponseEmpty,
} from "./WidgetTaskMultipleRenderTask";

import { graphql, useFragment } from "react-relay";

import { WidgetTaskCollectionSubscription } from "./__generated__/WidgetTaskCollectionSubscription.graphql";
import {
  WidgetTaskCollection_widgetTaskCollection$data,
  WidgetTaskCollection_widgetTaskCollection$key,
} from "./__generated__/WidgetTaskCollection_widgetTaskCollection.graphql";
import { useSubscriptionWithSkip } from "../utils/useSubscriptionWithSkip";
import { useSetWidgetFocus } from "../useSetFocus";

export const WidgetTaskCollection = ({
  widget: _widget,
  onEvent,
  interactive,
  isFocused,
  mode,
  storecastId,
  clientTitle,
}: {
  clientTitle?: string;
  widget: WidgetTaskCollection_widgetTaskCollection$key;
} & BaseWidgetProps) => {
  const widget = useFragment(
    graphql`
      fragment WidgetTaskCollection_widgetTaskCollection on WidgetTaskCollection {
        id
        title
        taskSections {
          id
          section {
            id
            title
          }
          task {
            id
            title
            routine {
              id
              title
            }
          }
          inProgressTaskExecution {
            ...WidgetTaskMultipleRenderTask_taskExecution
          }
        }
      }
    `,
    _widget
  );
  const setFocus = useSetWidgetFocus(widget.id, storecastId);
  const onPress = useCallback(() => {
    if (mode === "FOCUS" && !isFocused) {
      setFocus();
    } else {
      onEvent?.({
        kind: "WidgetTaskCollection",
        widgetId: widget.id,
      });
    }
  }, [isFocused, mode, onEvent, setFocus, widget.id]);

  useSubscriptionWithSkip<WidgetTaskCollectionSubscription>(
    useMemo(
      () => ({
        variables: {
          routineIds:
            widget?.taskSections?.map((ts) => ts.task.routine.id) ?? [],
          taskIds: widget?.taskSections?.map((ts) => ts.task.id) ?? [],
        },
        subscription: graphql`
          subscription WidgetTaskCollectionSubscription(
            $taskIds: [ID!]!
            $routineIds: [ID!]!
          ) {
            routineTaskExecutionChanged(
              routineIds: $routineIds
              taskIds: $taskIds
            ) {
              id
              taskSection {
                id
                inProgressTaskExecution {
                  ...WidgetTaskMultipleRenderTask_taskExecution
                }
              }
            }
          }
        `,
      }),
      [widget?.taskSections]
    ),
    {
      skip: interactive === false,
    }
  );

  const taskSections = useMemo(
    () => widget.taskSections?.slice(0, 8) ?? [],
    [widget.taskSections]
  );

  if (taskSections.length === 0) {
    return (
      <AHWidget>
        <AHWidgetTitle>{clientTitle ?? widget.title}</AHWidgetTitle>
        <AHWidgetNoData />
      </AHWidget>
    );
  }

  return (
    <TouchableBox style={{ flex: 1 }} color="surface" onPress={onPress}>
      <AHWidgetTitle>{clientTitle ?? widget.title}</AHWidgetTitle>
      <ResultGrid
        items={taskSections}
        renderRow={renderTaskResult}
        keyExtractor={keyExtractor}
      />
    </TouchableBox>
  );
};

function renderTaskResult(
  section: WidgetTaskCollection_widgetTaskCollection$data["taskSections"][number]
) {
  const taskTitle =
    (section.section.title ? section.section.title + ": " : "") +
    section.task.title;
  return section.inProgressTaskExecution ? (
    <WidgetTaskMultipleResponse
      key={section.id}
      taskTitle={taskTitle}
      routineTitle={section.task.routine.title}
      taskExecution={section.inProgressTaskExecution}
    />
  ) : (
    <WidgetTaskMultipleResponseEmpty
      key={section.id}
      taskTitle={taskTitle}
      routineTitle={section.task.routine.title}
    />
  );
}

function keyExtractor(task: { id: string }) {
  return task.id;
}
